import { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import { useGetQuery } from 'hooks';
import { useMapInstance } from './LeafletMap';

const WALL_ZOOM = 16;
const CELL_COUNT = 96;
const LEAFLET_TILE_PX = 256; // Leaflet tile grid is always 256px
const CELL_PX = 3; // canvas px per cell
const CANVAS_PX = CELL_COUNT * CELL_PX; // 288
const RANGE_METERS = 182.88; // 200 yards

const haversineMeters = (lat1, lng1, lat2, lng2) => {
  const R = 6371000;
  const φ1 = (lat1 * Math.PI) / 180;
  const φ2 = (lat2 * Math.PI) / 180;
  const Δφ = ((lat2 - lat1) * Math.PI) / 180;
  const Δλ = ((lng2 - lng1) * Math.PI) / 180;
  const a = Math.sin(Δφ / 2) ** 2 + Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

const tileCenterLatLng = (map, x, y) => {
  const pt = map.unproject(
    L.point((x + 0.5) * LEAFLET_TILE_PX, (y + 0.5) * LEAFLET_TILE_PX),
    WALL_ZOOM,
  );
  return { lat: pt.lat, lng: pt.lng };
};

const tileKey = (z, x, y) => `${z}/${x}/${y}`;

const getVisibleTiles = (map) => {
  const bounds = map.getBounds();
  const nwPx = map.project(bounds.getNorthWest(), WALL_ZOOM);
  const sePx = map.project(bounds.getSouthEast(), WALL_ZOOM);
  const minX = Math.floor(nwPx.x / LEAFLET_TILE_PX);
  const maxX = Math.floor(sePx.x / LEAFLET_TILE_PX);
  const minY = Math.floor(nwPx.y / LEAFLET_TILE_PX);
  const maxY = Math.floor(sePx.y / LEAFLET_TILE_PX);
  const tiles = [];
  for (let x = minX; x <= maxX; x++) {
    for (let y = minY; y <= maxY; y++) {
      tiles.push({ z: WALL_ZOOM, x, y });
    }
  }
  return tiles;
};

const tileToBounds = (map, x, y) => {
  const nw = map.unproject(L.point(x * LEAFLET_TILE_PX, y * LEAFLET_TILE_PX), WALL_ZOOM);
  const se = map.unproject(L.point((x + 1) * LEAFLET_TILE_PX, (y + 1) * LEAFLET_TILE_PX), WALL_ZOOM);
  return [[se.lat, nw.lng], [nw.lat, se.lng]];
};

const latlngToCell = (map, latlng, tileX, tileY) => {
  const px = map.project(latlng, WALL_ZOOM);
  const fracX = (px.x - tileX * LEAFLET_TILE_PX) / LEAFLET_TILE_PX;
  const fracY = (px.y - tileY * LEAFLET_TILE_PX) / LEAFLET_TILE_PX;
  const cx = Math.max(0, Math.min(CELL_COUNT - 1, Math.floor(fracX * CELL_COUNT)));
  const cy = Math.max(0, Math.min(CELL_COUNT - 1, Math.floor(fracY * CELL_COUNT)));
  return { cx, cy };
};

const mergePixels = (saved, pending) => {
  const map = new Map();
  saved.forEach((p) => map.set(`${p.x}_${p.y}`, p));
  pending.forEach((p) => map.set(`${p.x}_${p.y}`, p));
  return Array.from(map.values());
};

const renderTileCanvas = (pixels) => {
  const canvas = document.createElement('canvas');
  canvas.width = CANVAS_PX;
  canvas.height = CANVAS_PX;
  const ctx = canvas.getContext('2d');

  pixels.forEach(({ x, y, color }) => {
    ctx.fillStyle = color;
    ctx.fillRect(x * CELL_PX, y * CELL_PX, CELL_PX, CELL_PX);
  });

  ctx.strokeStyle = 'rgba(160,160,160,0.12)';
  ctx.lineWidth = 0.5;
  for (let i = 0; i <= CELL_COUNT; i++) {
    const pos = i * CELL_PX;
    ctx.beginPath(); ctx.moveTo(pos, 0); ctx.lineTo(pos, CANVAS_PX); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(0, pos); ctx.lineTo(CANVAS_PX, pos); ctx.stroke();
  }

  return canvas;
};

export const WallLayer = ({ active, pendingByTile, onCellDraw, onTileClick, refreshKey, userPosition, isPicking, onPickColor, isErasing, onEraseCell, onOutOfRange }) => {
  const map = useMapInstance();
  // tileKey → { overlay, savedPixels, x, y, z }
  const overlaysRef = useRef(new Map());
  const [visibleKeys, setVisibleKeys] = useState('');

  const onCellDrawRef = useRef(onCellDraw);
  const onTileClickRef = useRef(onTileClick);
  const pendingByTileRef = useRef(pendingByTile);
  const userPositionRef = useRef(userPosition);
  const isPickingRef = useRef(isPicking);
  const onPickColorRef = useRef(onPickColor);
  const isErasingRef = useRef(isErasing);
  const onEraseCellRef = useRef(onEraseCell);
  const onOutOfRangeRef = useRef(onOutOfRange);

  useEffect(() => { onCellDrawRef.current = onCellDraw; }, [onCellDraw]);
  useEffect(() => { onTileClickRef.current = onTileClick; }, [onTileClick]);
  useEffect(() => { pendingByTileRef.current = pendingByTile; }, [pendingByTile]);
  useEffect(() => { userPositionRef.current = userPosition; }, [userPosition]);
  useEffect(() => { isPickingRef.current = isPicking; }, [isPicking]);
  useEffect(() => { onPickColorRef.current = onPickColor; }, [onPickColor]);
  useEffect(() => { isErasingRef.current = isErasing; }, [isErasing]);
  useEffect(() => { onEraseCellRef.current = onEraseCell; }, [onEraseCell]);
  useEffect(() => { onOutOfRangeRef.current = onOutOfRange; }, [onOutOfRange]);

  // Track visible tile keys as map pans/zooms
  useEffect(() => {
    if (!map || !active) return;
    const update = () => {
      const keys = getVisibleTiles(map).map((t) => tileKey(t.z, t.x, t.y)).join(',');
      setVisibleKeys(keys);
    };
    update();
    map.on('moveend', update);
    map.on('zoomend', update);
    return () => {
      map.off('moveend', update);
      map.off('zoomend', update);
    };
  }, [map, active]); // eslint-disable-line react-hooks/exhaustive-deps

  // useGetQuery path is driven by setPath so URL changes properly track visibleKeys
  const queryState = useGetQuery({ url: '/wall-tiles?tiles=', enabled: false });
  const { data, setPath, setEnabled, reFetch } = queryState || {};

  // Keep query URL in sync with visible tiles
  useEffect(() => {
    if (!visibleKeys || !setPath) return;
    setEnabled(true);
    setPath(`/wall-tiles?tiles=${visibleKeys}`);
  }, [visibleKeys]); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-fetch after a save (refreshKey bumped by parent)
  useEffect(() => {
    if (refreshKey == null || !reFetch || !visibleKeys) return;
    reFetch(true);
  }, [refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Build / update overlays when visible tiles or fetched data changes
  useEffect(() => {
    if (!map || !active) {
      overlaysRef.current.forEach(({ overlay }) => overlay.remove());
      overlaysRef.current.clear();
      return;
    }

    const visibleSet = new Set(visibleKeys.split(',').filter(Boolean));
    const pixelsByTile = data?.tiles || {};

    // Remove tiles that scrolled out of view
    overlaysRef.current.forEach((entry, key) => {
      if (!visibleSet.has(key)) {
        entry.overlay.remove();
        overlaysRef.current.delete(key);
      }
    });

    // Add or update each visible tile
    visibleSet.forEach((key) => {
      const [z, x, y] = key.split('/').map(Number);
      const savedPixels = pixelsByTile[key] || [];
      const pending = pendingByTileRef.current?.[key] || [];
      const canvas = renderTileCanvas(mergePixels(savedPixels, pending));

      if (overlaysRef.current.has(key)) {
        const entry = overlaysRef.current.get(key);
        entry.savedPixels = savedPixels;
        entry.overlay.setUrl(canvas.toDataURL());
      } else {
        const bounds = tileToBounds(map, x, y);
        const overlay = L.imageOverlay(canvas.toDataURL(), bounds, {
          opacity: 1,
          interactive: true,
        }).addTo(map);

        const el = overlay.getElement();
        if (el) el.style.imageRendering = 'pixelated';

        overlay.on('click', (e) => {
          L.DomEvent.stopPropagation(e.originalEvent);
          if (onTileClickRef.current) {
            onTileClickRef.current();
            return;
          }
          const { cx, cy } = latlngToCell(map, e.latlng, x, y);
          if (isPickingRef.current) {
            const entry = overlaysRef.current.get(key);
            const pending = pendingByTileRef.current?.[key] || [];
            const hit = mergePixels(entry?.savedPixels || [], pending)
              .find((p) => p.x === cx && p.y === cy);
            onPickColorRef.current?.(hit?.color ?? null);
            return;
          }
          if (isErasingRef.current) {
            onEraseCellRef.current?.({ z, x, y, cx, cy });
            return;
          }
          const pos = userPositionRef.current;
          if (pos?.latitude) {
            const dist = haversineMeters(e.latlng.lat, e.latlng.lng, pos.latitude, pos.longitude);
            if (dist > RANGE_METERS) {
              onOutOfRangeRef.current?.();
              return;
            }
          }
          onCellDrawRef.current?.({ z, x, y, cx, cy });
        });

        const pos = userPositionRef.current;
        if (pos?.latitude) {
          const center = tileCenterLatLng(map, x, y);
          const dist = haversineMeters(center.lat, center.lng, pos.latitude, pos.longitude);
          overlay.setOpacity(dist <= RANGE_METERS ? 1.0 : 0.35);
        }

        overlaysRef.current.set(key, { overlay, savedPixels, x, y, z });
      }
    });
  }, [map, active, visibleKeys, data]); // eslint-disable-line react-hooks/exhaustive-deps

  // Update overlay opacity when user position changes
  useEffect(() => {
    if (!map || !userPosition?.latitude) return;
    overlaysRef.current.forEach((entry) => {
      const center = tileCenterLatLng(map, entry.x, entry.y);
      const dist = haversineMeters(center.lat, center.lng, userPosition.latitude, userPosition.longitude);
      entry.overlay.setOpacity(dist <= RANGE_METERS ? 1.0 : 0.35);
    });
  }, [map, userPosition?.latitude, userPosition?.longitude]); // eslint-disable-line react-hooks/exhaustive-deps

  // Update tile visuals when pending pixels change
  useEffect(() => {
    overlaysRef.current.forEach((entry, key) => {
      const pending = pendingByTile?.[key] || [];
      const canvas = renderTileCanvas(mergePixels(entry.savedPixels, pending));
      entry.overlay.setUrl(canvas.toDataURL());
    });
  }, [pendingByTile]); // eslint-disable-line react-hooks/exhaustive-deps

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      overlaysRef.current.forEach(({ overlay }) => overlay.remove());
      overlaysRef.current.clear();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return null;
};
